/**
 * Splits a comment body into text segments and mention segments.
 *
 * The API sends this field as an HTML fragment. The only expected element is
 * `<span class="mention">@Name</span>`. The caller renders every other part as
 * text, and React escapes text children.
 *
 * The parser is strict. Unexpected markup becomes a text segment. It shows as
 * literal text, and it does not run.
 */

import { decodeHtmlEntities } from './decodeHtmlEntities'

export type CommentSegment = {
  type: 'text' | 'mention'
  text: string
  /**
   * Offset of this segment in the original comment. The caller uses it as a
   * React key. It is stable and unique, unlike the position in the array.
   */
  start: number
}

const MENTION_SOURCE = '<span class="mention">(@[^<]*)</span>'

export const parseCommentSegments = (comment: string): CommentSegment[] => {
  if (!comment) return []

  // A shared global regex keeps lastIndex between calls. The results then
  // depend on the call order, so build the regex per call.
  const mentionPattern = new RegExp(MENTION_SOURCE, 'g')

  const segments: CommentSegment[] = []
  let lastIndex = 0
  let match = mentionPattern.exec(comment)

  while (match) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        text: decodeHtmlEntities(comment.slice(lastIndex, match.index)),
        start: lastIndex,
      })
    }

    segments.push({
      type: 'mention',
      text: decodeHtmlEntities(match[1]),
      start: match.index,
    })

    lastIndex = match.index + match[0].length
    match = mentionPattern.exec(comment)
  }

  if (lastIndex < comment.length) {
    segments.push({
      type: 'text',
      text: decodeHtmlEntities(comment.slice(lastIndex)),
      start: lastIndex,
    })
  }

  return segments
}
